import React, { useEffect, useRef, useState } from "react";
import { formatDate, formatDateTime } from "../../utilities/globalServiceUtils";
import { useSite } from "../../context/siteContext";
import { Alert, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import Datagrid from "../Datagrid/Datagrid";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { useCart } from "../../context/cartContext";
import PageTitle from "../PageTitle/PageTitle";
import OrderTracker from "../OrderTracker/OrderTracker";
import OrderDetails from "../OrderDetails/OrderDetails";
import ContextFilterAndSort from "../ContextFilterAndSort/ContextFilterAndSort";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "./AnalyticsTables.css";
import * as XLSX from "xlsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { BUTTON, PLACEHOLDER, TABLE_HEADERS, PAGE_TITLES } from "../../constants";
import { ASSETS_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";
import { getAnalyticsOrders } from "../../services/analyticsChartService";
import { toast } from "../../context/ToastContext";
const AnalyticsTables = ({
	loading,
	chartFilters = {},
	setLoading,
	selectedRange,
	additionalFilters,
	setAdditionalFilters,
	setShowCharts,
	selectedMetric,
	setSelectedMetric,
}) => {
	const { selectedSite, currencySymbol } = useSite();
	const { addToCart, addMultipleToCart } = useCart();
	const [metrics, setMetrics] = useState([]);
	const [orders, setOrders] = useState([]);
	const [fullOrdersData, setFullOrdersData] = useState([]);
	const [filteredOrders, setFilteredOrders] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedOrder, setSelectedOrder] = useState(null);
	const [error, setError] = useState(null);
	const lastQueryRef = useRef("");
	const {t} = useTranslation();
	const triggerToast = (message, severity = "success") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};

    // use global formatters

	const filterOrders = (orders) => {
		if (
			selectedMetric.key === "total_orders" ||
			selectedMetric.key === "total_revenue" ||
			selectedMetric.key === "AOV"
		) {
			return orders;
		}
		return orders.filter((order) => order.order.order_status === selectedMetric.key);
	};

	useEffect(() => {
		const queryKey = JSON.stringify(chartFilters || {});
		if (queryKey === lastQueryRef.current) return;
		lastQueryRef.current = queryKey;

		const fetchOrders = async (req_body) => {
			setLoading(true);
			try {
				const reqBody = {
					...req_body,
				};
				console.log('Fetch Orders', reqBody)
				const response = await getAnalyticsOrders(reqBody);
				console.log("In analytics tables getanalyticsorders response",response);
				if (response.status) {
					const orderDetailsArray = response.data;
					console.log("Orders Length", orderDetailsArray.length);

                    const allOrders = filterOrders(orderDetailsArray).map((data) => ({
                        order_id: data.order.order_id,
                        order_number: data.order.order_number,
                        user_display_name: data.order.user_display_name,
                        site_number: data.order.customer_site_number,
                        order_total: data.order.order_total,
                        order_status: data.order.order_status,
                        order_created_date: formatDateTime(data.order.order_created_date),
                        order_update_date: formatDateTime(data.order.order_update_date),
                        order_currency: data.order.order_currency,
                        order_payment_mode: data.order.order_payment_mode,
                        order_po: data?.details?.po_number || "",
                        order_po_file: data?.details?.po_filename || "",
                        products: data.products,
                    }));

					console.log("All Orders", allOrders);

					const ids = allOrders.map((order) => order.order_id);
					const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);
					console.log("Duplicate IDs: ", duplicates);
					console.log("Non Duplicate IDs: ", ids);

					setFullOrdersData(orderDetailsArray);
					setOrders(allOrders);
					setFilteredOrders(allOrders);
					setError(null);
					setSelectedOrder(null);
					await aggregateOrderData(response);
				} else {
					setError(response.data.message || t('UI_TEXT.NO_ORDERS_FOR_SITE'));
				}
			} catch (error) {
				console.error("Error while fetching the order", error);
				setError(t('UI_TEXT.ERROR_OCCURRED_WHILE_FETCHING_ORDER'));
			} finally {
				setLoading(false);
			}
		};

		if (Object.keys(chartFilters).length > 0) {
			fetchOrders(chartFilters);
		}
		console.log(Object.keys(chartFilters).length);
	}, [chartFilters, selectedMetric]);

	const aggregateOrderData = async (order_data) => {
		const count = Number.isFinite(Number(order_data?.count))
			? Number(order_data.count)
			: Array.isArray(order_data?.data)
				? order_data.data.length
				: 0;
		if (count !== 0) {
			const order_metrics = order_data.order_metrics;
			const trends = order_data.trends;
			await updateMetrics(order_metrics, trends);
		} else {
			await updateMetrics();
		}
	};

	const flattenObjectToString = (obj) => {
		let flatString = "";

		// Recursively traverse the object and build a flat string of values
		const flatten = (value) => {
			if (typeof value === "object" && value !== null) {
				Object.values(value).forEach(flatten);
			} else {
				flatString += ` ${value}`;
			}
		};

		flatten(obj);
		return flatString.toLowerCase(); // Convert to lowercase for case-insensitive search
	};

	const handleSearchChange = (value) => {
		const query = value.toLowerCase();
		setSearchQuery(query);

		// Filter orders based on the search query
		const filtered = orders.filter((order) => flattenObjectToString(order).includes(query));

		setFilteredOrders(filtered);
	};
	const handleViewOrder = (order) => {
		setSelectedOrder(order); // Set the selected order to display its details
	};

	const handleCloseOrderDetails = () => {
		setSelectedOrder(null);
	};

	const renderOrderDetails = () => {
		if (!selectedOrder) return null;
		const filteredOrder = fullOrdersData.find((order) => order.order.order_id === selectedOrder.order_id);
		const { order } = filteredOrder;

		filteredOrder.products = Array.from(
			new Map(filteredOrder.products.map((product) => [product.product_id, product])).values()
		);

		return (
			<div>
				<PageTitle title={`Order Details: ${order.order_number}`} />
				<div className="track-order-container">
					<Button variant="outlined" color="primary" onClick={handleCloseOrderDetails}>
						{t('BUTTON.CLOSE')}
					</Button>
					<Button
						variant="contained"
						startIcon={<AddShoppingCartIcon />}
						color="primary"
						onClick={() => {
							handleOrderAgain(selectedOrder);
						}}>
						{t('BUTTON.BUY_AGAIN')}
					</Button>
				</div>
				<div className="order-tracker-container"></div>
				<OrderTracker orderData={filteredOrder} />
				<OrderDetails orderData={filteredOrder} />

				<div className="all-orders-container">
					<Button
						variant="contained"
						startIcon={<AddShoppingCartIcon />}
						color="primary"
						onClick={() => {
							handleOrderAgain(selectedOrder);
						}}>
						{t('BUTTON.BUY_AGAIN')}
					</Button>
					<Button variant="outlined" color="primary" onClick={handleCloseOrderDetails}>
						{t('BUTTON.CLOSE')}
					</Button>
				</div>
			</div>
		);
	};

	const exportOrdersToXLSX = (allOrders, selectedSite) => {
		const sheetData = [];
		const merges = [];

		// Define headers
		const headers = [
			t('ORDERS.ORDER_ID'),
			t('TABLE_HEADERS.ORDER_NUMBER'),
			t('TABLE_HEADERS.USERS'),
			t('TABLE_HEADERS.SITE_NUMBER'),
			t('TABLE_HEADERS.TOTAL'),
			t('TABLE_HEADERS.STATUS'),
			t('TABLE_HEADERS.CREATED_DATE'),
			t('TABLE_HEADERS.UPDATED_DATE'),
			t('TABLE_HEADERS.CURRENCY'),
			t('TABLE_HEADERS.PAYMENT_MODE'),
			t('TABLE_HEADERS.PO_NUMBER'),
			t('TABLE_HEADERS.PRODUCT_NAME'),
			t('UI_TEXT.PRODUCT_PRICE'),
			t('TABLE_HEADERS.QUANTITY'),
			t('TABLE_HEADERS.SITE'),
		];
		sheetData.push(headers);

		let rowIndex = 1; // Start from 1 due to header

		allOrders.forEach((order) => {
			const seen = new Set();
			const products =
				order.products?.filter((product) => {
					const id = product.product_id;
					const purchasable = product.productsData?.purchasable === 1;
					if (purchasable && !seen.has(id)) {
						seen.add(id);
						return true;
					}
					return false;
				}) || [];

			products.forEach((product, idx) => {
				const row = [];

				if (idx === 0) {
					// First product row: include order-level info
					row.push(
						order.order_id,
						order.order_number,
						order.user_display_name,
						order.site_number,
						order.order_total,
						order.order_status,
						order.order_created_date,
						order.order_update_date,
						order.order_currency,
						order.order_payment_mode,
						order.order_po
					);
				} else {
					// Leave cells empty — will be merged
					for (let i = 0; i < 11; i++) row.push(null);
				}

				// Product-level data
				row.push(
					product.productsData.product_name || t('GENERAL.N_A'),
					`${currencySymbol}${product.productsData.product_sell_price}` || t('GENERAL.N_A'),
					product.product_quantity,
					selectedSite
				);

				sheetData.push(row);
				rowIndex++;
			});

			if (products.length > 1) {
				// Merge the order-level cells for this order's rows
				for (let col = 0; col <= 10; col++) {
					merges.push({
						s: { r: rowIndex - products.length, c: col }, // start row/col
						e: { r: rowIndex - 1, c: col }, // end row/col
					});
				}
			}
		});

		const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
		worksheet["!merges"] = merges;

		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, t('ORDERS.ORDERS'));
		XLSX.writeFile(workbook, `${t('ORDERS.ORDERS_EXPORT')}_${chartFilters.startDate}_to_${chartFilters.endDate}.xlsx`);
	};

	const handleOrderAgain = async (row) => {
		console.log(row);
		const products = row.products;
		const cartAdd = products
			.map((product) => {
				if (product.productsData && product.productsData.purchasable === 1) {
					return { ProductID: product.product_id, Quantity: product.product_quantity, Site: selectedSite };
				}
				// console.log(product);
				return null; // Return null explicitly for non-purchasable products
			})
			.filter((item) => item !== null); // Remove null values from the array

		if (cartAdd.length === 0) {
			console.warn(t('CART.NO_VALID_PRODUCTS_TO_ADD_TO_CART'));
			return;
		}
		await addMultipleToCart(cartAdd);
		triggerToast(`Part(s) from ${row.order_number} have been added to Cart`, "success");
	};

	// Define columns for the Datagrid
	const columns = [
		{
			field: "order_number",
			headerName: t('TABLE_HEADERS.ORDER_NUMBER'),
			flex: 1,
			renderCell: (params) => (
				<Tooltip title={t('PLACEHOLDER.ORDER_DETAILS')}>
					<span
						style={{
							cursor: "pointer",
							fontWeight: "bold",
						}}
						onMouseEnter={(e) => (e.target.style.fontWeight = "bold")}
						onMouseLeave={(e) => (e.target.style.fontWeight = "bold")}>
						{params.value}
					</span>
				</Tooltip>
			),
		},
		{ field: "order_payment_mode", headerName: t('TABLE_HEADERS.PAYMENT_MODE') },
        { field: "order_po", headerName: t('TABLE_HEADERS.PO'), width: 150 },
        {
            field: "order_po_file",
            headerName: t('UI_TEXT.PO_FILE'),
            width: 160,
            renderCell: (params) => (params.value ? (
                <a href={`${ASSETS_BASE_URL.replace(/\/$/, '')}/po_uploads/${params.value}`} target="_blank" rel="noreferrer">{params.value}</a>
            ) : '')
        },
		{ field: "user_display_name", headerName: t('TABLE_HEADERS.USERS') },
		{ field: "order_total", headerName:t('TABLE_HEADERS.TOTAL'), width: 120 },
		{
			field: "order_currency",
			headerName: t('TABLE_HEADERS.CURRENCY'),
			width: 100,
			renderCell: (params) => params.value?.toUpperCase() || t('UI_TEXT.USD'),
		},
		{ field: "order_status", headerName: t('TABLE_HEADERS.STATUS'), width: 120 },
		{
			field: "order_created_date",
			headerName: t('TABLE_HEADERS.CREATED_DATE'),
			valueFormatter: (value) => formatDateTime(value),
			width: 150,
		},
		{
			headerName: t('TABLE_HEADERS.ORDER_AGAIN'),
			width: 100,
			sortable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params) => (
				<div>
					{params.row.site_number === selectedSite && (
						<Tooltip title={t('TABLE_HEADERS.ORDER_AGAIN')}>
							<IconButton
								color="primary"
								aria-label="Order Again"
								onClick={(event) => {
									event.stopPropagation();
									handleOrderAgain(params.row);
								}}>
								<AddShoppingCartIcon />
							</IconButton>
						</Tooltip>
					)}
				</div>
			),
		},
	];

	const handleRowClick = (params) => {
		handleViewOrder(params.row);
	};

	const capitalizeFirstLetter = (str) => {
		return str.charAt(0).toUpperCase() + str.slice(1);
	};

	const updateMetrics = async (order_metrics = {}, trends = {}) => {
		const hasData = Object.keys(order_metrics).length > 0 && Object.keys(trends).length > 0;

		const formatCurrency = (value) => {
			const absValue = Math.abs(value);
			let formattedValue;
			let suffix = "";

			if (absValue >= 1e9) {
				formattedValue = value / 1e9;
				suffix = "B";
			} else if (absValue >= 1e6) {
				formattedValue = value / 1e6;
				suffix = "M";
			} else if (absValue >= 1e3) {
				formattedValue = value / 1e3;
				suffix = "K";
			} else {
				formattedValue = value;
			}

			return `${currencySymbol}${formattedValue.toFixed(2)}${suffix}`;
		};

		const createMetric = (title, value, change, changeType, key) => ({
			title,
			value,
			change: `${change}%`,
			changeType,
			key,
		});

		const defaultMetrics = [
			createMetric(t('METRICS.TOTAL_EARNINGS'), formatCurrency(0), "0", "warning", "total_revenue"),
			createMetric(t('METRICS.TOTAL_ORDERS'), "0", "0", "warning", "total_orders"),
			// createMetric("Shipped Orders", "0", "0", "warning"),
			createMetric(t('METRICS.PENDING_ORDERS'), "0", "0", "warning", "Pending"),
			createMetric(t('METRICS.INVOICE_ORDERS'), "0", "0", "warning", "Invoiced"),
			createMetric(t('METRICS.DELIVERED_ORDERS'), "0", "0", "warning", "Delivered"),
			createMetric(t('METRICS.AVERAGE_ORDER_VALUE'), formatCurrency(0), "0", "warning", "AOV"),
		];

		if (!hasData) {
			setMetrics(defaultMetrics);
			return;
		}

		const statusMetrics = ["pending", "invoiced", "delivered"].map((status) =>
			createMetric(
				`${capitalizeFirstLetter(status)} Orders`,
				`${order_metrics.total_orders_by_status[status]}`,
				`${trends.total_orders_by_status[status].change}`,
				trends.total_orders_by_status[status].trend,
				capitalizeFirstLetter(status)
			)
		);

		setMetrics([
			createMetric(
				t('METRICS.TOTAL_EARNINGS'),
				formatCurrency(order_metrics.total_revenue),
				`${trends.total_revenue.change}`,
				trends.total_revenue.trend,
				"total_revenue"
			),
			createMetric(
				t('METRICS.TOTAL_ORDERS'),
				`${order_metrics.total_orders}`,
				`${trends.total_orders.change}`,
				trends.total_orders.trend,
				"total_orders"
			),
			...statusMetrics,
			createMetric("AOV", formatCurrency(order_metrics.aov), `${trends.aov.change}`, trends.aov.trend, "AOV"),
		]);
	};

	const handleShowTables = (metric) => {
		console.log("metric", metric);

		setSelectedMetric(metric);
		setShowCharts(false);
	};

	return (
		<Box className="chart-container" sx={{ px: 5 }} data-tour="analytics-data-point">
			<Box className="chart-row" sx={{ maxHeight: "125px !important" }}>
				{metrics.map((metric, index) => (
					<Box
						key={index}
						className="chart-card"
						onClick={() => {
							console.log("metric", metric);
							handleShowTables(metric);
						}}
						sx={{
							borderBottom:
								selectedMetric.title === metric.title
									? (theme) => `4px solid ${theme.palette.primary.main}`
									: "none",
						}}>
						<Box>
							<Typography variant="h6">{metric.title}</Typography>
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									columnGap: "15px",
									alignItems: "flex-start",
									justifyContent: "space-between",
									mt: 1,
								}}>
								<Typography variant="h5" color="info">
									{metric.value}
								</Typography>
							</Box>
						</Box>
						<Box>
							{metric.changeType === "success" ? (
								<>
									<TrendingUpIcon color="success" sx={{ fontSize: "30px" }} />
									<Typography variant="body1" color="success">
										{metric.change}
									</Typography>
								</>
							) : metric.changeType === "error" ? (
								<>
									<TrendingDownIcon color="error" sx={{ fontSize: "30px" }} />
									<Typography variant="body1" color="error">
										{metric.change}
									</Typography>
								</>
							) : (
								<>
									<TrendingFlatIcon color="info" sx={{ fontSize: "30px" }} />
									<Typography variant="body1" color="info">
										{metric.change}
									</Typography>
								</>
							)}
						</Box>
					</Box>
				))}
			</Box>
			<Box className="chart-row" sx={{ maxHeight: "fit-content !important" }}>
				<Button
					variant="text"
					color="primary"
					onClick={() => setShowCharts(true)}
					startIcon={<ArrowBackIcon />}>
					Back
				</Button>
			</Box>
			<div className="orders-container">
				{/* <Box sx={{ mb: 2 }}>
					<Typography variant="h6">Orders</Typography>
					<Typography variant="body1">
						{t('GENERAL.SHOWING_1')} {filteredOrders.length} orders out of {fullOrdersData.length}
					</Typography>
				</Box> */}
				<PageTitle
					title={PAGE_TITLES.getFilteredTitle(selectedMetric?.title, t)}
                    subtitle={PAGE_TITLES.getFilteredSubtitle(filteredOrders.length, fullOrdersData.length, t)}
				/>
				<div className="orders-serach-container" width="100%" justifyContent="space-between">
					<Box sx={{ width: { xs: "100%", md: "20%" } }}>
						<ContextFilterAndSort
							value={searchQuery}
							onSearchChange={handleSearchChange}
							placeholder= {t('PLACEHOLDER.SEARCH_ORDERS')}
							showSearch={true}
							showSort={false}
						/>
					</Box>
					<Button
						variant="contained"
						color="primary"
						size="small"
						startIcon={<FileDownloadIcon />}
						onClick={() => exportOrdersToXLSX(filteredOrders, selectedSite)}>
						{t('BUTTON.EXPORT_ORDERS')}
					</Button>
				</div>
				<Datagrid
					key="orderHistoryGrid"
					data={filteredOrders}
					columns={columns}
					rowClick={true}
					onRowClick={handleRowClick}
					pageSize={5}
					showSearchBar={false}
					checkboxSelection={false}
					rowIdField="order_id"
					sortBy={[{ field: "order_created_date", sort: "desc" }]}
				/>
				{renderOrderDetails()}
			</div>
		</Box>
	);
};

export default AnalyticsTables;
